import React from 'react';
import { Shield, Eye, Edit, Users, LucideIcon } from 'lucide-react';
import Card from '../Card';

interface RoleCardProps {
    title: string;
    description: string;
    permissions: string[];
    variant?: 'system' | 'project' | 'programme';
    icon?: 'admin' | 'member' | 'viewer' | 'editor' | 'default';
}

/**
 * RoleCard - Display individual role with icon, title, description, and permissions
 * Used in SystemRolesPage to show role definitions
 * 
 * Feature: System Administration Interface (001-admin-settings)
 * User Story: US1 - View System Role Definitions
 */
const RoleCard: React.FC<RoleCardProps> = ({
    title,
    description,
    permissions,
    variant = 'system',
    icon = 'default'
}) => {
    // Icon mapping
    const getIcon = (): LucideIcon => {
        switch (icon) {
            case 'admin':
                return Shield;
            case 'editor':
                return Edit;
            case 'viewer':
                return Eye;
            case 'member':
                return Users;
            default:
                return Shield;
        }
    };

    // Color scheme based on variant
    const getColorScheme = () => {
        switch (variant) {
            case 'system':
                return {
                    bg: 'bg-primary-50 dark:bg-primary-900/20',
                    border: 'border-primary-200 dark:border-primary-800',
                    iconBg: 'bg-primary-100 dark:bg-primary-800/50',
                    iconColor: 'text-primary-600 dark:text-primary-400',
                    badge: 'bg-primary-100 dark:bg-primary-800 text-primary-800 dark:text-primary-200'
                };
            case 'project':
                return {
                    bg: 'bg-secondary-50 dark:bg-secondary-900/20',
                    border: 'border-secondary-200 dark:border-secondary-800',
                    iconBg: 'bg-secondary-100 dark:bg-secondary-800/50',
                    iconColor: 'text-secondary-600 dark:text-secondary-400',
                    badge: 'bg-secondary-100 dark:bg-secondary-800 text-secondary-800 dark:text-secondary-200'
                };
            case 'programme':
                return {
                    bg: 'bg-accent-50 dark:bg-accent-900/20',
                    border: 'border-accent-200 dark:border-accent-800',
                    iconBg: 'bg-accent-100 dark:bg-accent-800/50',
                    iconColor: 'text-accent-600 dark:text-accent-400',
                    badge: 'bg-accent-100 dark:bg-accent-800 text-accent-800 dark:text-accent-200'
                };
        }
    };

    const IconComponent = getIcon();
    const colors = getColorScheme();

    return (
        <Card variant="elevated" className={`border-2 ${colors.border} ${colors.bg}`}>
            <Card.Body>
                <div className="space-y-4">
                    {/* Header with icon and title */}
                    <div className="flex items-start gap-4">
                        <div className={`p-3 rounded-lg ${colors.iconBg}`}>
                            <IconComponent className={`w-6 h-6 ${colors.iconColor}`} />
                        </div>
                        <div className="flex-1">
                            <h3 className="text-lg font-bold text-gray-900 dark:text-white">
                                {title}
                            </h3>
                            <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                                {description}
                            </p>
                        </div>
                    </div>

                    {/* Permissions list */}
                    {permissions.length > 0 && (
                        <div className="pt-3 border-t border-gray-200 dark:border-gray-700">
                            <h4 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                                Permissions
                            </h4>
                            <ul className="space-y-2">
                                {permissions.map((permission, index) => (
                                    <li
                                        key={index}
                                        className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300"
                                    >
                                        <span className={`inline-block w-1.5 h-1.5 rounded-full mt-1.5 ${colors.iconColor.replace('text-', 'bg-')}`} />
                                        <span>{permission}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </Card.Body>
        </Card>
    );
};

export default RoleCard;
